'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { TAWKTO_ENABLED, TAWKTO_SCRIPT_URL } from './tawkto-config';

const EXCLUDED_PUBLIC_PREFIXES = [
  '/cv/',
  '/portfolio/',
  '/r/',
  '/bio/',
  '/resume/',
  '/about/',
  '/page/',
  '/p/',
] as const;

function isExcludedPath(pathname: string): boolean {
  return EXCLUDED_PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function hideTawktoWidget(): void {
  window.Tawk_API?.hideWidget?.();
}

function loadTawkToScript(): void {
  if (typeof window === 'undefined' || document.getElementById('tawkto-script')) {
    return;
  }

  window.Tawk_API = window.Tawk_API || ({} as NonNullable<Window['Tawk_API']>);
  window.Tawk_LoadStart = new Date();

  // Hide the widget as soon as Tawk.to finishes initializing so it never
  // flashes on initial page loads or full-page refreshes.
  window.Tawk_API.onLoad = () => {
    hideTawktoWidget();
    const tawkApi = window.Tawk_API;
    if (tawkApi) {
      tawkApi.onChatMinimized = () => {
        tawkApi.hideWidget?.();
      };
    }
  };

  const script = document.createElement('script');
  script.id = 'tawkto-script';
  script.async = true;
  script.src = TAWKTO_SCRIPT_URL;
  script.charset = 'UTF-8';
  script.setAttribute('crossorigin', '*');

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  }
}

export function TawktoWidget(): null {
  const pathname = usePathname();
  const isLoadedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !TAWKTO_ENABLED || isExcludedPath(pathname)) {
      return;
    }

    if (isLoadedRef.current) {
      return;
    }
    isLoadedRef.current = true;

    loadTawkToScript();

    // If Tawk.to is already initialized (e.g. after a client-side navigation),
    // hide it immediately rather than waiting for the onLoad callback.
    if (window.Tawk_API) {
      hideTawktoWidget();
      window.Tawk_API.onChatMinimized = () => {
        window.Tawk_API?.hideWidget?.();
      };
    }

    // Fallback: keep the widget hidden if it later becomes minimized.
    const hideInterval = window.setInterval(() => {
      if (window.Tawk_API?.isChatMinimized?.()) {
        hideTawktoWidget();
      }
    }, 2000);

    return () => {
      window.clearInterval(hideInterval);
    };
  }, [pathname]);

  return null;
}
